/**
 * Type guard to check if value is FormData
 */
function isFormData(value: any): value is FormData {
  return value instanceof FormData;
}

/**
 * Type guard to check if value is a File
 */
function isFile(value: any): value is File {
  return value instanceof File;
}

/**
 * Type guard to check if value is a Blob
 */
function isBlob(value: any): value is Blob {
  return value instanceof Blob;
}

/**
 * Universal wrapper for both regular objects and FormData
 * Provides a unified interface for property access and modification
 */
export default class FormDataWrapper<T extends Record<string, any> | FormData> {
  private data: T;
  private proxy: any;

  constructor(data: T) {
    this.data = data;
    this.proxy = this.createProxy();
  }

  /**
   * Creates a Proxy that intercepts property access and modification
   */
  private createProxy(): any {
    return new Proxy(this, {
      get: (target, prop: string | symbol) => {
        // Handle internal methods and properties
        if (prop in target) {
          return (target as any)[prop];
        }

        // Handle property access
        if (typeof prop === 'string') {
          return this.get(prop);
        }
      },
      set: (target, prop: string | symbol, value: any) => {
        // Handle property setting
        if (typeof prop === 'string') {
          this.set(prop, value);
          return true;
        }
        return false;
      },
      has: (target, prop: string | symbol) => {
        if (typeof prop === 'string') {
          return this.has(prop);
        }
        return prop in target;
      },
      deleteProperty: (target, prop: string | symbol) => {
        if (typeof prop === 'string') {
          return this.delete(prop);
        }
        return false;
      }
    });
  }

  /**
   * Get a property value
   * For FormData with multiple values, returns the first one
   * Use getAll() for multiple values
   */
  get(key: string): any {
    if (isFormData(this.data)) {
      return this.data.get(key);
    } else {
      return (this.data as Record<string, any>)[key];
    }
  }

  /**
   * Get all values for a key (useful for FormData with multiple files)
   */
  getAll(key: string): any[] {
    if (isFormData(this.data)) {
      return this.data.getAll(key);
    } else {
      const value = (this.data as Record<string, any>)[key];
      return value === undefined ? [] : [value];
    }
  }

  /**
   * Set a property value
   * For FormData: replaces existing values
   */
  set(key: string, value: any): void {
    if (!isFormData(this.data)) {
      (this.data as Record<string, any>)[key] = value;
      return;
    }

    // Delete existing entries for this key
    this.data.delete(key);

    // Handle different value types
    if (value === undefined || value === null) {
      // Don't append null/undefined to FormData
      return;
    }
    
    if (Array.isArray(value)) {
      // Handle arrays (multiple files or values)
      value.forEach(item => {
        if (isFile(item) || isBlob(item)) {
          this.data.append(key, item);
        } else {
          this.data.append(key, String(item));
        }
      });
      return;
    }
    
    if (isFile(value) || isBlob(value)) {
      // Handle single file/blob
      this.data.append(key, value);
      return;
    }
    
    // Handle regular values
    this.data.append(key, String(value));
  }

  /**
   * Append a value (useful for adding multiple files with same key)
   * For regular objects, converts value to array if needed
   */
  append(key: string, value: any): void {
    if (isFormData(this.data)) {
      if (isFile(value) || isBlob(value)) {
        this.data.append(key, value);
      } else if (value !== undefined && value !== null) {
        this.data.append(key, String(value));
      }
      return;
    }

    const existing = (this.data as Record<string, any>)[key];
    if (existing === undefined) {
      (this.data as Record<string, any>)[key] = value;
    } else if (Array.isArray(existing)) {
      existing.push(value);
    } else {
      (this.data as Record<string, any>)[key] = [existing, value];
    }
  }

  /**
   * Set a file or files
   */
  setFile(key: string, file: File | File[] | Blob | Blob[]): void {
    this.set(key, file);
  }

  /**
   * Get file(s) - returns null if not a file
   */
  getFile(key: string): File | null {
    const value = this.get(key);
    return isFile(value) ? value : null;
  }

  /**
   * Get all files for a key
   */
  getFiles(key: string): File[] {
    const values = this.getAll(key);
    return values.filter(isFile);
  }

  /**
   * Check if a property exists
   */
  has(key: string): boolean {
    if (isFormData(this.data)) {
      return this.data.has(key);
    }

    return key in (this.data as Record<string, any>);
  }

  /**
   * Delete a property
   */
  delete(key: string): boolean {
    if (isFormData(this.data)) {
      this.data.delete(key);
      return true;
    }

    return delete (this.data as Record<string, any>)[key];
  }

  /**
   * Get all keys
   */
  keys(): string[] {
    if (!isFormData(this.data)) {
      return Object.keys(this.data as Record<string, any>);
    }

    const keysSet = new Set<string>();
    for (const [key] of (this.data as any).entries()) {
      keysSet.add(key);
    }
    return Array.from(keysSet);
  }

  /**
   * Get all values
   */
  values(): any[] {
    if (!isFormData(this.data)) {
      return Object.values(this.data as Record<string, any>);
    }

    const values: any[] = [];
    for (const [, value] of (this.data as any).entries()) {
      values.push(value);
    }
    return values;
  }

  /**
   * Get all entries as key-value pairs
   */
  entries(): Array<[string, any]> {
    if (isFormData(this.data)) {
      return Array.from((this.data as any).entries());
    }

    return Object.entries(this.data as Record<string, any>);
  }

  /**
   * Convert to plain object
   */
  toObject(): Record<string, any> {
    if (!isFormData(this.data)) {
      return { ...(this.data as Record<string, any>) };
    }

    const obj: Record<string, any> = {};
    for (const [key, value] of (this.data as any).entries()) {
      // Handle multiple values for same key
      if (obj[key] !== undefined) {
        if (!Array.isArray(obj[key])) {
          obj[key] = [obj[key]];
        }
        obj[key].push(value);
      } else {
        obj[key] = value;
      }
    }
    return obj;
  }

  /**
   * Get file information without loading file content
   */
  getFileInfo(key: string): Array<{ name: string; size: number; type: string }> | null {
    const files = this.getFiles(key);
    if (files.length === 0) return null;
    
    return files.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type
    }));
  }

  /**
   * Check if a property contains file(s)
   */
  isFile(key: string): boolean {
    const value = this.get(key);
    return isFile(value) || isBlob(value);
  }

  /**
   * Get the original data
   */
  getRawData(): T {
    return this.data;
  }

  /**
   * Get the proxied version for direct property access
   */
  getProxy(): any {
    return this.proxy;
  }
}